import React, { useEffect, useMemo, useState } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Company, Order, Transaction, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);
import DataTable from "@/components/molecules/DataTable";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { useGetCompaniesByIdsPublic } from "@/services/Company";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import { weightUnitOf } from "@/utils/weight";

interface OrderTransactionsTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<Transaction, "price">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
  /** Required order to display transactions for **/
  order: Order;
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const OrderTransactionsTable: React.FC<OrderTransactionsTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination: _pagination = true,
  actionable: _actionable = true,
  initialRowsPerPage = 15,
  order,
}) => {
  const [companyIds, setCompanyIds] = useState<number[]>([]);

  const { t } = useTranslation();

  const companies = useGetCompaniesByIdsPublic(companyIds, {
    enabled: companyIds.length > 0,
  });

  useEffect(() => {
    if ((order && order.orderType.id === OrderTypeEnum.SELL) || order.orderType.id === OrderTypeEnum.AUCTION) {
      const buyerCompany = order.transactions?.map((x: any) => {
        return x.settlement.settlement.buyer.companyId;
      });
      if (buyerCompany !== undefined) {
        setCompanyIds(buyerCompany);
      }
    }
  }, [order]);

  useEffect(() => {
    if (order && order.orderType.id === OrderTypeEnum.BUY) {
      const sellerCompany = order.transactions?.map((x: any) => {
        return x.settlement.settlement.seller.companyId;
      });
      if (sellerCompany !== undefined) {
        setCompanyIds(sellerCompany);
      }
    }
  }, [order]);

  const baseColumns: ColumnDef<Transaction>[] = useMemo(
    () => [
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("orders:transactionDate")} />,
        id: "transactionDate",
        cell: ({ row }) => {
          return dayjs(row.original.transactionDate).format("Do MMMM YYYY");
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("dashboard:status")} />,
        id: "status",
        cell: ({ row }) => {
          const trs = row.original;
          return trs.finalAmount === 0 && order.amountDeviation !== 0
            ? "-"
            : t("enumerables:orderStatus." + trs.status.toUpperCase());
        },
      },
      {
        header: ({ column }) => {
          return order.orderType.id === OrderTypeEnum.BUY ? (
            <NonSortableHeader column={column} label={t("orders:seller")} />
          ) : (
            <NonSortableHeader column={column} label={t("orders:buyer")} />
          );
        },
        id: order.orderType.id === OrderTypeEnum.BUY ? "sellerCompanyId" : "buyerCompanyId",
        cell: ({ row }) => {
          const trs = row.original;
          if (order.orderType.id === OrderTypeEnum.SELL || order.orderType.id === OrderTypeEnum.AUCTION) {
            return trs.finalAmount === 0 && order.amountDeviation !== 0
              ? "-"
              : companies?.find((company: Company) => company.id === trs.settlement.settlement.buyer.companyId)
                  ?.companyName || "-";
          } else if (order.orderType.id === OrderTypeEnum.BUY) {
            return trs.finalAmount === 0 && order.amountDeviation !== 0
              ? "-"
              : companies?.find((company: Company) => company.id === trs.settlement.settlement.seller.companyId)
                  ?.companyName || "-";
          } else {
            return "";
          }
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:price")} />,
        id: "price",
        cell: ({ row }) => {
          const trs = row.original;
          return trs.finalAmount === 0 && order.amountDeviation !== 0
            ? "-"
            : trs.settlement.settlement.buyer.totalPrice.toFixed(2) + " " + trs.settlement.settlement.buyer.currency;
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:amount")} />,
        id: "amount",
        cell: ({ row }) => {
          const trs = row.original;
          // A weight order's final amount can differ from the ordered amount even at 0 % deviation, as the backend's
          // check has replaced the deviation limit for kg and tonnes.
          if (weightUnitOf(order.unit?.unit) !== null && trs.finalAmount > 0) return trs.finalAmount + trs.unit;
          return trs.finalAmount === 0 && order.amountDeviation !== 0
            ? "-"
            : order.amountDeviation && order.amountDeviation > 0
              ? trs.finalAmount > 0
                ? trs.finalAmount + trs.unit
                : "-"
              : trs.amount > 0
                ? trs.amount + trs.unit
                : "-";
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("orders:transactionDone")} />,
        id: "transactionClosed",
        cell: ({ row }) => {
          const trs = row.original;
          return trs.finalAmount === 0 && order.amountDeviation !== 0
            ? "-"
            : trs.transactionClosed !== null
              ? dayjs(trs.transactionClosed).format("DD.MM.YYYY")
              : "-";
        },
      },
    ],
    [t, order]
  );

  const columns = useVisibleColumns<Transaction, "price">(baseColumns, visibleColumns);

  return (
    <DataTable
      onRowClick={undefined}
      data={order.transactions || []}
      columns={columns}
      isLoading={false}
      mobileHeaderCell={0}
      showPagination={false}
      initialSort={initialSort ?? undefined}
      initialRowsPerPage={initialRowsPerPage}
    />
  );
};

export default OrderTransactionsTable;
